import React, { useEffect, useState } from 'react';
import { Button, Col, Container, Row, Form } from 'react-bootstrap';
import Select from 'react-select';
import { BUSSINESS_SOURCE_OPTIONS, ENDPOINTS } from '../../../../helpers/constants';
import './style.scss';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../../../helpers/api';
import { isStaff, onCreateApiRequestData } from '../../../../helpers/global';
import { Modal, ProgressBar } from 'react-bootstrap';
import useAppChoices from '../../../../hooks/useAppChoices';

const AddOrganizationForm = ({ name, adminEmail, adminName, setItemActionMeta, orgs, setOrgs, setNewOrg }) => {
  const sourceOptions = BUSSINESS_SOURCE_OPTIONS.map(option => ({ value: option, label: option }));
  const users = useAppChoices('users') || [];
  const requiredUsers = users
    .filter(user => isStaff(user.role) && !user.organization)
    .map(user => ({ ...user, value: user._id, label: user.name }));

  const [itemAdding, setItemAdding] = useState(false);
  const [formData, setFormData] = useState({
    name: name || '',
    estoppelProcessingFee: '',
    businessSource: sourceOptions[0].value,
    leadGenerator: requiredUsers[0]?._id || null,
    lienSearchProcessingFee: '',
    lienSearchMinimumInvoicableAmount: 275,
    surveyProcessingFee: '',
    surveyMinimumInvoicableAmount: 275,
    estoppelMinimumInvoicableAmount: 275,
    accountManager: requiredUsers[0]?._id || null,
    email: '',
    adminName: adminName,
    adminEmail: adminEmail
  });

  useEffect(() => {
    if (name) {
      setFormData({ ...formData, name: name });
    }
  }, [name]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSourceSelect = e => {
    setFormData({
      ...formData,
      businessSource: e.value
    });
  };

  const handleLeadGeneratorSelect = e => {
    setFormData({
      ...formData,
      leadGenerator: e?.value || ''
    });
  };

  const handleAccountManagerSelect = e => {
    setFormData({
      ...formData,
      accountManager: e?.value || null
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const { adminName, adminEmail, ...organizationData } = formData;
    const newAdmin = {
      name: adminName,
      email: adminEmail,
      phone: null,
      defaultProcessorFor: null
    };

    setItemAdding(true);

    const { response, error } = await makeRESTApiRequest(
      onCreateApiRequestData('Organization', ENDPOINTS.CREATE_ORGANIZATIONS, {
        ...organizationData,
        newAdmin
      })
    );

    setItemAdding(false);
    if (error) {
      toast.error(error);
      return;
    }

    setOrgs([response, ...orgs]);
    setNewOrg(response);
    setItemActionMeta(null);
    toast.success('Organization Successfully Added!!');
  };

  return (
    <Modal size="lg" centered backdrop="static" show={!!name} onHide={() => setItemActionMeta(null)}>
      <Modal.Header closeButton>
        <Modal.Title>Add New Organization</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Container>
          <Form onSubmit={handleSubmit} className="px-4">
            <Row>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Name</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Lien Search Processing Fee</Form.Label>
                  <Form.Control
                    type="number"
                    name="lienSearchProcessingFee"
                    value={formData.lienSearchProcessingFee}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Estoppel Processing Fee</Form.Label>
                  <Form.Control
                    type="number"
                    name="estoppelProcessingFee"
                    value={formData.estoppelProcessingFee}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Survey Processing Fee</Form.Label>
                  <Form.Control
                    type="number"
                    name="surveyProcessingFee"
                    value={formData.surveyProcessingFee}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="mb-3">
              <h6 className="mb-2 font-weight-bold text-secondary">Minimum Invoicable Amount</h6>
              <Row>
                <Col md={4}>
                  <Form.Group className="midFont mb-2">
                    <Form.Label className="required">Lien Search</Form.Label>
                    <Form.Control
                      type="number"
                      name="lienSearchMinimumInvoicableAmount"
                      value={formData.lienSearchMinimumInvoicableAmount}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="midFont mb-2">
                    <Form.Label className="required">Estoppel</Form.Label>
                    <Form.Control
                      type="number"
                      name="estoppelMinimumInvoicableAmount"
                      value={formData.estoppelMinimumInvoicableAmount}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="midFont mb-2">
                    <Form.Label className="required">Survey</Form.Label>
                    <Form.Control
                      type="number"
                      name="surveyMinimumInvoicableAmount"
                      value={formData.surveyMinimumInvoicableAmount}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
            <Row>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Business Source</Form.Label>
                  <Select
                    id="businessSource"
                    className="basic-single select-organization"
                    classNamePrefix="select"
                    defaultValue={sourceOptions[0]}
                    options={sourceOptions}
                    onChange={handleSourceSelect}
                    isSearchable={false}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Account Manager</Form.Label>
                  <Select
                    id="accountManager"
                    className="basic-single select-organization"
                    classNamePrefix="select"
                    defaultValue={requiredUsers[0]}
                    options={requiredUsers}
                    onChange={handleAccountManagerSelect}
                    isClearable
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Lead Generator</Form.Label>
                  <Select
                    id="leadGenerator"
                    className="basic-single select-organization"
                    classNamePrefix="select"
                    defaultValue={requiredUsers[0]}
                    options={requiredUsers}
                    onChange={handleLeadGeneratorSelect}
                    isClearable
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="midFont mb-2">
                  <Form.Label className="required">Email</Form.Label>
                  <Form.Control type="email" name="email" value={formData.email} onChange={handleChange} required />
                </Form.Group>
              </Col>
            </Row>
            <hr />
            <div>
              <h3>Organization Admin</h3>
              <Row>
                <Col md={6}>
                  <Form.Group className="midFont mb-2">
                    <Form.Label className="required">Your Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="adminName"
                      value={formData.adminName}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="midFont mb-2">
                    <Form.Label className="required">Your Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="adminEmail"
                      value={formData.adminEmail}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
            <div className="w-100 d-flex justify-content-end mt-4">
              {!itemAdding && (
                <Button type="submit" variant="primary" className="px-4">
                  Add
                </Button>
              )}
            </div>
          </Form>
        </Container>
        {itemAdding && <ProgressBar variant="dark" now={100} animated striped label="Saving organization...." />}
      </Modal.Body>
    </Modal>
  );
};

export default AddOrganizationForm;
